import React from 'react';
import { Pressable } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Stack } from '@/components/layout';
import { Checkbox, FormInput, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { AuthTextField } from '@/features/auth/components/auth-text-field';
import { GuardianProfileActionHeader } from '@/features/guardian-profile/components';
import { useGuardianDeleteAccount } from '@/features/guardian-profile/hooks';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors, withOpacity } from '@/theme/colors';

const strings = STRINGS.guardianProfile.deleteAccount;

export function GuardianDeleteAccountScreen() {
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();

  const {
    reason,
    acknowledged,
    password,
    secureTextEntry,
    passwordError,
    canSubmit,
    reasonMaxLength,
    setSecureTextEntry,
    onChangeReason,
    onToggleAcknowledged,
    onChangePassword,
    handleBack,
    handleDelete,
  } = useGuardianDeleteAccount();

  return (
    <Stack flex={1} backgroundColor={guardianColors['1']}>
      <GuardianProfileActionHeader
        title={strings.title}
        isTablet={isTablet}
        topInset={insets.top}
        onBack={handleBack}
      />

      <KeyboardAwareScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.content,
          isTablet ? styles.contentTablet : styles.contentPhone,
          { paddingBottom: insets.bottom + scale(24) },
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        bottomOffset={scale(12)}
        extraKeyboardSpace={scale(12)}>
        <Stack style={isTablet ? styles.formTablet : styles.formPhone} gap={scale(20)}>
          {/* Warning banner — always displayed */}
          <Stack direction="row" align="flex-start" gap={scale(8)} style={styles.warningBanner}>
            <SvgIcon name="IcWarning" size={scale(20)} color={guardianColors.Error} />
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
              style={styles.warningText}>
              {strings.warning}
            </Text>
          </Stack>

          {/* Reason (optional, max 200 chars) */}
          <Stack gap={scale(4)}>
            <Text variant="Font Guardian App/Subtitle/Subtitle 12 Semibold" style={styles.label}>
              {strings.reasonLabel}
            </Text>
            <FormInput
              label={strings.reasonLabel}
              hideLabel
              inputVariant="Font Guardian App/Subtitle/Subtitle 14 Regular"
              inputStyle={styles.reasonText}
              contentStyle={styles.reasonContent}
              value={reason}
              onChangeText={onChangeReason}
              placeholder={strings.reasonPlaceholder}
              multiline
              numberOfLines={4}
              maxLength={reasonMaxLength}
            />
          </Stack>

          <Stack gap={scale(12)}>
            {/* Acknowledgement checkbox (must be checked) */}
            <Checkbox checked={acknowledged} onValueChange={onToggleAcknowledged}>
              <Text variant="Inter/Paragraph/X Small" style={styles.acknowledgeLabel}>
                {strings.acknowledgement}
              </Text>
            </Checkbox>

            {/* Password (required, with show/hide toggle) */}
            <AuthTextField
              label={strings.passwordLabel}
              placeholder={strings.passwordPlaceholder}
              value={password}
              onChangeText={onChangePassword}
              secureTextEntry={secureTextEntry}
              onSecureTextEntryChange={setSecureTextEntry}
              error={passwordError ?? undefined}
              fieldProps={{
                maxLength: 50,
                autoCapitalize: 'none',
                autoCorrect: false,
                textContentType: 'password',
                autoComplete: 'current-password',
              }}
            />
          </Stack>

          {/* Permanently Delete — disabled until acknowledged + password entered */}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={strings.submit}
            accessibilityState={{ disabled: !canSubmit }}
            disabled={!canSubmit}
            onPress={handleDelete}
            style={({ pressed }) => [
              styles.deleteButton,
              !canSubmit ? styles.deleteDisabled : null,
              pressed && canSubmit ? styles.pressed : null,
            ]}>
            <SvgIcon name="IcDelete" size={scale(20)} color={guardianColors.Error} />
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
              style={styles.deleteLabel}>
              {strings.submit}
            </Text>
          </Pressable>
        </Stack>
      </KeyboardAwareScrollView>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  scroll: {
    flex: 1,
    backgroundColor: guardianColors['1'],
  },
  content: {
    flexGrow: 1,
  },
  contentPhone: {
    paddingTop: scale(12),
    paddingHorizontal: scale(16),
  },
  contentTablet: {
    paddingTop: scale(16),
    alignItems: 'center',
  },
  formPhone: {
    width: '100%',
  },
  formTablet: {
    width: '100%',
    maxWidth: '40%',
  },
  warningBanner: {
    borderRadius: scale(12),
    borderCurve: 'continuous',
    paddingHorizontal: scale(8),
    paddingVertical: scale(12),
    borderWidth: scale(0.5),
    borderColor: colors['Level 3'],
    backgroundColor: withOpacity('#FF2D2D', 0.08),
  },
  warningText: {
    flex: 1,
    color: guardianColors['10'],
  },
  label: {
    color: guardianColors['10'],
  },
  reasonContent: {
    borderColor: guardianColors['3'],
    borderRadius: scale(8),
    height: scale(76),
    alignItems: 'flex-start',
    paddingLeft: scale(12),
    paddingRight: scale(12),
    paddingVertical: scale(8),
  },
  reasonText: {
    color: guardianColors['10'],
    marginLeft: 0,
    height: '100%',
    textAlignVertical: 'top',
  },
  acknowledgeLabel: {
    flex: 1,
    color: guardianColors['10'],
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: scale(8),
    height: scale(48),
    marginTop: scale(4),
    borderRadius: scale(12),
    borderCurve: 'continuous',
    borderWidth: 1,
    borderColor: colors['Level 3'],
    backgroundColor: colors['0'],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  deleteDisabled: {
    opacity: 0.5,
  },
  deleteLabel: {
    color: guardianColors.Error,
  },
  pressed: {
    opacity: 0.7,
  },
}));

export default GuardianDeleteAccountScreen;
